'use client';

import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { MolstarWrapper } from "@loschmidt/molstar";
import { StateObjectSelector } from "molstar/lib/mol-state/object";
import { setSubtreeVisibility } from "molstar/lib/mol-plugin/behavior/static/state";
import { lock } from "./utils";
import { ColorNames } from 'molstar/lib/mol-util/color/names';
import { Ligand } from "./types";
import { useRemoveObject } from "./hooks";

export type LigandState = {
  trajectorySelector: StateObjectSelector,
  reprSelector: StateObjectSelector,
  ligand: Ligand,
}

export function useLigands(
  viewer: MolstarWrapper | undefined,
  ligands: Ligand[] | undefined,
) {

  const ligandStatesRef = useRef<Record<string, LigandState>>({});

  const setLigandVisibility = useCallback(async (selector: StateObjectSelector, visible: boolean) => {
    if (viewer) {
      const hidden = !visible;
      setSubtreeVisibility(viewer.plugin.state.data, selector.ref, hidden);
    }
  }, [viewer]);

  const getLigandKey = useCallback((ligand: Ligand) => ligand.url, []);

  const removeObject = useRemoveObject(viewer);

  const renderLigandsEffectBody = useCallback(async (cancelledRef: MutableRefObject<boolean>) => {
    if (!viewer || cancelledRef.current) {
      return;
    }
    const all: Record<string, { state?: LigandState, ligand?: Ligand }> = {};
    Object.entries(ligandStatesRef.current).forEach(([key, state]) => {
      all[key] = { state };
    })
    ligands?.forEach(ligand => {
      const key = getLigandKey(ligand);
      all[key] ??= {};
      all[key].ligand = ligand;
    })

    const toUpdate: [string, LigandState, Ligand][] = [];
    const toRemove: [string, LigandState][] = [];
    const toAdd: [string, Ligand][] = [];

    for (const key in all) {
      const {state, ligand} = all[key];
      if (state && ligand) {
        if (state.ligand !== ligand) {
          // Ligand is NOT updated when props are changed while ligand is hidden.
          if (state.ligand.visible || ligand.visible) {
            toUpdate.push([key, state, ligand]);
          }
        }
      } else if (state) {
        toRemove.push([key, state]);
      } else if (ligand) {
        // Ligand is rendered only if it's visible.
        // This behavior simplifies MolstarViewer props.
        // A parent can pass many ligands, but they'll be rendered only after they are set to visible.
        // Then, "visible" controls visibility, while a ligand is still in the viewer.
        if (ligand.visible !== false) {
          toAdd.push([key, ligand]);
        }
      }
    }

    console.debug(`[MolstarViewer] Ligands to add: ${toAdd.length}, to remove: ${toRemove.length}, to update: ${toUpdate.length}`);

    const promises: Promise<void>[] = [];

    for (const [key, ligand] of toAdd) {
      promises.push((async () => {
        let data: Awaited<ReturnType<typeof viewer.plugin.builders.data.rawData>>;
        data = await viewer.plugin.builders.data.download({
          url: ligand.url,
          isBinary: false,
          label: ligand.label,
        }, {state: {isGhost: true}});

        if (cancelledRef.current) {
          return;
        }

        const trajectory = await viewer.plugin.builders.structure.parseTrajectory(data, ligand.format);
        if (!trajectory.isOk) {
          return;
        }

        const model = await viewer.plugin.builders.structure.createModel(trajectory);
        if (!model.isOk) {
          return;
        }

        const structure = await viewer.plugin.builders.structure.createStructure(model);
        if (!structure.isOk) {
          return;
        }

        const component = await viewer.plugin.builders.structure.tryCreateComponentStatic(structure, 'ligand');
        if (!component?.isOk) {
          return;
        }

        const update = viewer.plugin.state.data.build();
        const builder = viewer.plugin.builders.structure.representation;

        const representation = builder.buildRepresentation(
          update,
          component,
          {
            type: 'ball-and-stick',
            color: 'element-symbol',
            colorParams: {
              carbonColor: { name: 'element-symbol', params: {} },
              colors: {name: 'custom', params: {'C': ColorNames.magenta}},
            },
          },
          { tag: ligand.label }
        );
        await update.commit({ revertOnError: true });

        ligandStatesRef.current = {
          ...ligandStatesRef.current,
          [key]: {
            ligand,
            trajectorySelector: trajectory,
            reprSelector: representation,
          },
        };
      })());
    }

    for (const [key, state] of toRemove) {
      promises.push((async () => {
        await removeObject(state.trajectorySelector);
        if (ligandStatesRef.current[key]?.trajectorySelector === state.trajectorySelector) {
          // Selector didn't change, we can remove it now.
          // (The condition should always be true, but who knows...)
          delete ligandStatesRef.current[key];
        }
      })());
    }

    for (const [key, state, ligand] of toUpdate) {
      promises.push((async () => {
        if (state.ligand.visible !== ligand.visible) {
          await setLigandVisibility(state.reprSelector, ligand.visible ?? true);
        }
        ligandStatesRef.current = {
          ...ligandStatesRef.current,
          [key]: {
            ...ligandStatesRef.current[key],
            ligand,
          },
        }
      })());
    }

    for (const p of promises) {
      await p.catch(err => console.error(err))
    }

  }, [viewer, ligands, getLigandKey, removeObject]);

  useEffect(() => {
    const cancelledRef = {current: false};
    // noinspection JSIgnoredPromiseFromCall
    lock.acquire('RENDER_LIGANDS_EFFECT', async () => {
      await renderLigandsEffectBody(cancelledRef);
    });
    return () => {
      cancelledRef.current = true;
    }
  }, [renderLigandsEffectBody]);

  const focusLigand = useCallback((ligand: Ligand) => {
    if (viewer) {
      const key = getLigandKey(ligand);
      const state = ligandStatesRef.current[key];
      if (state) {
        viewer.plugin.managers.camera.focusRenderObjects(
          state.reprSelector.data?.repr.renderObjects
        );
      }
    }
  }, [viewer, getLigandKey]);

  return {
    focusLigand,
  };
}

